import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Session as SupabaseSession } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { apiFetch } from "./api-client";
import { queryKeys } from "./query-keys";
import type { SessionDTO } from "@colab/shared";

type AuthContextValue = {
  supabaseSession: SupabaseSession | null;
  session: SessionDTO | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [supabaseSession, setSupabaseSession] = useState<SupabaseSession | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSupabaseSession(data.session);
      setBootstrapping(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => {
      setSupabaseSession(next);
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  // Profile + role + permissions live on the worker so the role-permissions
  // join uses the same RLS scope on every request. Mirrors the legacy
  // `getSession()` helper in /lib/auth.ts.
  const { data: session, isLoading: profileLoading } = useQuery<SessionDTO | null>({
    queryKey: queryKeys.auth.me(),
    queryFn: async () => {
      if (!supabaseSession) return null;
      return apiFetch<SessionDTO>("/auth/me");
    },
    enabled: !bootstrapping,
  });

  const signOut = async () => {
    await apiFetch<{ ok: true }>("/auth/signout", { method: "POST" });
    await supabase.auth.signOut();
    queryClient.clear();
  };

  return (
    <AuthContext.Provider
      value={{
        supabaseSession,
        session: session ?? null,
        loading: bootstrapping || profileLoading,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export function useSession() {
  return useAuth().session;
}
